import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, Filter, Mail, MessageCircle, FileText, Settings, LogOut, Send, Menu, X, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/customers", label: "Customers", icon: Users, id: "customers" },
  { to: "/audiences", label: "Audiences", icon: Filter, id: "audiences" },
  { to: "/email-campaigns", label: "Email Campaigns", icon: Mail, id: "email-campaigns" },
  { to: "/whatsapp-campaigns", label: "WhatsApp Campaigns", icon: MessageCircle, id: "whatsapp-campaigns" },
  { to: "/templates", label: "Templates", icon: FileText, id: "templates" },
  { to: "/team", label: "Team", icon: UserCog, id: "team", adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = (
    <nav className="flex-1 px-3 py-4 space-y-1">
      {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map(({ to, label, icon: Icon, id, end }) => (
        <NavLink
          key={id}
          to={to}
          end={end}
          data-testid={`sidebar-nav-${id}`}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
              isActive ? "bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-500/30" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
            }`
          }
        >
          <Icon size={17} strokeWidth={2} />
          {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-[#0B0F17]">
      <aside className="hidden lg:flex w-64 flex-shrink-0 flex-col border-r border-slate-800 bg-[#0D1220] sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-800 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-indigo-500 flex items-center justify-center">
            <Send size={17} className="text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-slate-50 leading-tight">SND Marketing</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-400">Data-driven reach</div>
          </div>
        </div>
        {nav}
        <div className="px-4 py-4 border-t border-slate-800">
          <div className="text-sm text-slate-300 truncate flex items-center gap-2">
            {user?.name}
            <span data-testid="user-role-badge" className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${user?.role === "admin" ? "bg-indigo-500/15 text-indigo-400" : "bg-slate-700/50 text-slate-400"}`}>{user?.role}</span>
          </div>
          <div className="text-xs text-slate-500 truncate mb-2">{user?.email}</div>
          <Button
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
            onClick={async () => { await logout(); navigate("/auth"); }}
          >
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 py-3 bg-[#0D1220]/95 backdrop-blur-md border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center"><Send size={15} className="text-white" /></div>
          <span className="font-heading font-bold text-slate-50">SND Marketing</span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="text-slate-300 p-1">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-[#0B0F17] pt-16 flex flex-col overflow-y-auto">
          {nav}
          <div className="px-4 py-4 border-t border-slate-800">
            <div className="text-sm text-slate-300 truncate flex items-center gap-2">
              {user?.name}
              <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${user?.role === "admin" ? "bg-indigo-500/15 text-indigo-400" : "bg-slate-700/50 text-slate-400"}`}>{user?.role}</span>
            </div>
            <div className="text-xs text-slate-500 truncate mb-2">{user?.email}</div>
            <Button
              data-testid="mobile-logout-button"
              variant="outline"
              size="sm"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={async () => { await logout(); navigate("/auth"); }}
            >
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-y-auto pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
